import React, { useRef } from "react";
import { Col, NMForm, AppErrorBoundary, NMWaitIndicator } from "@neumetric/blueprint-lib/components";
import { NMObjectHeader } from "@neumetric/blueprint-lib/flows";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { mitigatorActions, getCurrentRisk, isRiskArchived, getDecisionList, getSeverityList, getRiskStatusList } from "../../../store/";
import { ControlsSection, RiskAcceptanceSection } from "./helper";
import { InfoSection } from "./Info";

export const CreateNewRisk = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { riskId } = useParams();
  const location = useLocation();
  const currentRisk = useSelector(getCurrentRisk);
  const decisionList = useSelector(getDecisionList);
  const severityList = useSelector(getSeverityList);
  const statusList = useSelector(getRiskStatusList);
  const _infoRef = useRef(null);
  const _controlsRef = useRef(null);
  const _headerRef = useRef(null);
  const _acceptanceRef = useRef(null);
  const _internalNotesRef = useRef(null);
  const showViewMode = Boolean(riskId && currentRisk);
  const isArchived = useSelector(isRiskArchived);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);
  //added for default values
  const DEFAULT_INTERNAL_NOTES = "No Internal Notes provided.";

  const onComplete = (status) => {
    setIsSubmitting(false);
    let search = status ? `?tab=${status}` : location.search;
    search = search ? search : "?tab=draft";
    navigate({ pathname: "..", search: search }, { replace: true });
  };

  const handleCreateRisk = () => {
    const _headerValid = _headerRef.current.validate();
    const isInternalNotesValid = _internalNotesRef.current.validate();
    const isInfoValid = _infoRef.current.validate();
    const isAcceptanceValid = _acceptanceRef.current.validate();
    if (!_headerValid || !isInternalNotesValid || !isInfoValid || !isAcceptanceValid) {
      return;
    }
    const headerValues = _headerRef.current.getValues();
    const infoValues = _infoRef.current.getValues();
    const controlsValues = _controlsRef.current.getValues();
    const acceptanceValues = _acceptanceRef.current.getValues();
    const internalNotesValues = _internalNotesRef.current.getValues();
    const body = {
      ...headerValues,
      ...internalNotesValues,
      ...infoValues,
      ...controlsValues,
      ...acceptanceValues,
      status: "draft",
    };
    const action = mitigatorActions.createRisk({ risk: body });
    action.onComplete = onComplete;
    dispatch(action);
  };

  const handleUpdateSave = () => {
    const _headerValid = _headerRef.current.validate();
    const isInternalNotesValid = _internalNotesRef.current.validate();
    const isInfoValid = _infoRef.current.validate();
    if (!_headerValid || !isInternalNotesValid || !isInfoValid) {
      return;
    }
    const headerValues = _headerRef.current.getValues();
    const internalNotesValues = _internalNotesRef.current.getValues();
    const infoValues = _infoRef.current.getValues();
    const values = {
      ...headerValues,
      ...internalNotesValues,
      ...infoValues,
    };
    const body = { ...values, guid: currentRisk.guid };
    const action = mitigatorActions.updateRisk(body);
    setIsSubmitting(true);
    action.onComplete = () => {
      setIsEditMode(false);
      onComplete();
    };
    dispatch(action);
  };

  React.useEffect(() => {
    if (riskId) {
      if (!currentRisk) {
        dispatch(mitigatorActions.fetchRisk({ risk_id: riskId }));
      }
    } else {
      setIsEditMode(true);
    }
  }, [riskId, currentRisk]);


  const handleEdit = () => {
    setIsEditMode(true);
  };

  const getSaveButtonProps = React.useCallback(() => {
    if (isArchived) {
      return undefined
    }
    return {
      variant: showViewMode ? "CAUTION" : "ACTION",
      onClick: showViewMode ? handleUpdateSave : handleCreateRisk,
    };
  }, [isArchived, showViewMode]);

  const handleMenuClick = (menu) => {
    const status = menu.id === "archive" ? "archive" : "active";
    const body = { ...currentRisk, status: status };
    const action = mitigatorActions.updateRisk(body);
    action.onComplete = () => {
      onComplete(status);
    };
    dispatch(action);
    return;
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  const getOptions = (attribute) => {
    if (attribute === "decision") {
      const opts = decisionList;
      return opts;
    }
    if (attribute === "severity") {
      const opts = severityList;
      return opts;
    }
    if (attribute === "risk_status") {
      const opts = statusList;
      return opts;
    }
  };

  const handleDecisionAutoUpdate = (values) => {
    _headerRef.current.updateFormData(values);
  };

  const menuItems = React.useMemo(() => {
    let items = [];
    if (showViewMode && currentRisk) {
      if (currentRisk.status !== "archive") {
        if (currentRisk.status === "draft") {
          items.push({
            id: "activate",
            label: "Activate",
            icon: "CHECK_CONFIRM",
            variant: "CONTRAST",
          });
        }
        items.push({
          id: "archive",
          label: "Archive",
          icon: "OPEN_FOLDER",
          variant: "CAUTION",
        });
      } else {
        items = null;
      }
    } else {
      items = null;
    }
    return items;
  }, [showViewMode, currentRisk]);

  return (
    <AppErrorBoundary>
      {(!riskId || (riskId && currentRisk)) ? (
        <Col className={`w-100 h-100 overflow-hide new-risk exo2-all ${isEditMode ? "" : "risk-info-form"}`}>
          <NMObjectHeader
            object={currentRisk || { severity: "Minimal" }}
            classes={{
              root: "no-shadow",
            }}
            initialMode={!showViewMode ? "EDIT" : "VIEW"}
            objectProps={{
              name: "risk",
              idAttribute: "risk_id",
              titleAttribute: "title",
              placeholder: "Provide a Title",
            }}
            ref={_headerRef}
            formData={currentRisk}
            fields={[]}
            menu={menuItems ? {
              headerButton: { btnType: "CONTAINED", icon: "DOT_MENU" },
              items: menuItems,
              onMenuClick: handleMenuClick,
            } : undefined}
            isSubmitting={isSubmitting}
            itemsOrder={["priority", "severity", "status", "menu"]}
            getOptions={getOptions}
            severity={{
              attribute: "severity",
              placeholder: "Severity",
              editPolicy: "READONLY",
              classes: { root: "w-150-px" }
            }}
            status={{
              attribute: "risk_status",
              placeholder: isEditMode ? "Status" : "Undefined",
              classes: { root: "w-210-px mr-12" },
            }}
            priority={{
              attribute: "decision",
              label: "Decision",
              placeholder: isEditMode ? "Decision" : "Undefined",
              classes: { root: `mr-12 w-200-px` },
            }}
            edit={showViewMode ? { onClick: handleEdit } : undefined}
            save={getSaveButtonProps()}
            cancel={showViewMode ? { onClick: handleCancel } : undefined}
          />
          <Col className="w-100 flex-auto overflow-auto pl-20 pr-20">
            <NMForm
              ref={_internalNotesRef}
              fields={[
                {
                  attribute: "internal_notes",
                  label: "Internal Notes",
                  typeRepresentation: "TEXTAREA",
                  rows: 3,
                  placeholder: isEditMode ? "Provide a short summary of the context in which this Risk is applicable." : DEFAULT_INTERNAL_NOTES,
                  required: true,
                  defaultValue: DEFAULT_INTERNAL_NOTES,
                  classes: { root: "w-100 mt-24" },
                },
              ]}
              formData={currentRisk || {}}
              readOnly={!isEditMode}
            />
            <InfoSection ref={_infoRef} editMode={isEditMode} handleDecisionAutoUpdate={handleDecisionAutoUpdate} />
            <ControlsSection ref={_controlsRef} />
            <RiskAcceptanceSection ref={_acceptanceRef} />
          </Col>
        </Col>
      ) : (
        <NMWaitIndicator size="small" />
      )}
    </AppErrorBoundary>
  );
};
